import type { Plan, Account } from '@/types/planning';
import type { planningPlanAccountsApi } from '../planning-api';
import { Dispatch } from '@/redux';

interface OptimisticUpdateParams {
  dispatch: Dispatch;
  api: typeof planningPlanAccountsApi;
  payload?: Partial<UpdatePlanPayload>;
  planId: string;
  contactId?: string;
  accountId?: string;
  accounts?: Account[];
}

interface UpdateCacheParams extends OptimisticUpdateParams {
  updateFn: (draft: Plan) => void;
}

interface OptimisticUpdateResult {
  undo: () => void;
  rollback?: () => void;
}

interface UpdatePlanPayload extends Plan {
  importedAccountIds: string[];
}

const NO_OP_PATCH = { undo: () => {} };

const updateTimestamp = (draft: Plan) => {
  (draft as any)._updatedAt = Date.now();
};

const hasValidAccounts = (draft: Plan) => !!(draft && draft.accounts && Array.isArray(draft.accounts));

const updateAccountsWithImportedStatus = (draft: Plan, importedAccountIds?: string[]) => {
  if (Array.isArray(importedAccountIds) && draft.accounts) {
    draft.accounts = draft.accounts.map(account => (importedAccountIds.includes(account._id) ? { ...account, imported: true } : account));
  }
};

const updateAccountImportedStatus = (draft: Plan, accountId: string, imported: boolean) => {
  if (!hasValidAccounts(draft)) {
    return false;
  }

  const accountIndex = draft.accounts.findIndex(acc => acc && acc._id === accountId);
  if (accountIndex === -1) {
    return false;
  }

  draft.accounts = draft.accounts.map(account => (account._id === accountId ? { ...account, imported } : account));

  return true;
};

const setAllAccountsImportedStatus = (draft: Plan, imported: boolean) => {
  if (hasValidAccounts(draft)) {
    draft.accounts = draft.accounts.map(account => ({
      ...account,
      imported,
    }));
  }
};

const updatePlanWithPayload = (draft: Plan, payload: Partial<UpdatePlanPayload>) => {
  Object.assign(draft, payload);
  updateAccountsWithImportedStatus(draft, payload.importedAccountIds);
  updateTimestamp(draft);
};

const updateCache = ({ dispatch, api, planId, contactId, updateFn }: UpdateCacheParams) => {
  if (!planId) {
    return [NO_OP_PATCH];
  }

  const patches: OptimisticUpdateResult[] = [];

  try {
    const planPatch = dispatch(api.util.updateQueryData('getPlan', planId, updateFn));
    patches.push(planPatch);
  } catch (error) {
    patches.push(NO_OP_PATCH);
  }

  if (contactId) {
    try {
      const planPatch = dispatch(api.util.updateQueryData('getPlanForContact', contactId, updateFn));
      patches.push(planPatch);
    } catch (error) {
      patches.push(NO_OP_PATCH);
    }
  }

  return patches;
};

/**
 * Optimistically updates a plan with the given payload
 * Updates both plan cache and contact plan cache if contactId is provided
 */
export const optimisticUpdatePlan = ({ dispatch, api, planId, contactId, payload }: OptimisticUpdateParams) =>
  updateCache({
    dispatch,
    api,
    planId,
    contactId,
    updateFn: (draft: Plan) => {
      updatePlanWithPayload(draft, payload || {});
    },
  });

/**
 * Optimistically clears plan contact and sets all accounts imported = false
 * Updates both plan cache and contact plan cache if contactId is provided
 */
export const optimisticClearPlanContact = ({ dispatch, api, planId, contactId }: OptimisticUpdateParams) =>
  updateCache({
    dispatch,
    api,
    planId,
    contactId,
    updateFn: (draft: Plan) => {
      draft.contactId = undefined;
      setAllAccountsImportedStatus(draft, false);
      updateTimestamp(draft);
    },
  });

/**
 * Optimistically deletes a plan account (sets imported = false)
 * Updates both plan cache and contact plan cache if contactId is provided
 */
export const optimisticDeletePlanAccount = ({ dispatch, api, planId, accountId, contactId }: OptimisticUpdateParams) => {
  if (!planId || !accountId) {
    return [NO_OP_PATCH];
  }

  return updateCache({
    dispatch,
    api,
    planId,
    contactId,
    updateFn: (draft: Plan) => {
      updateAccountImportedStatus(draft, accountId, false);
      updateTimestamp(draft);
    },
  });
};

/**
 * Optimistically links a plan to a contact
 * Updates both plan cache and contact plan cache
 */
export const optimisticLinkPlanToContact = ({ dispatch, api, planId, contactId }: OptimisticUpdateParams) =>
  updateCache({
    dispatch,
    api,
    planId,
    contactId,
    updateFn: (draft: Plan) => {
      draft.contactId = contactId;
      setAllAccountsImportedStatus(draft, false);
      updateTimestamp(draft);
    },
  });
